import React, { useState, useEffect } from 'react';
import { Transaction, ToastMessage } from '../../types';
import { Check, Printer, AlertTriangle } from 'lucide-react';
import qz from 'qz-tray';
import Logo from '@/assets/niamia_logo.png';
import { connectQZ } from '@/utils';

interface ReceiptModalProps {
  transaction: Transaction | null;
  onClose: () => void;
  addToast: (type: ToastMessage['type'], title: string, message: string) => void;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ transaction, onClose, addToast }) => {
  const [logoBase64, setLogoBase64] = useState<string>('');

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const response = await fetch(Logo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (mounted) setLogoBase64(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Logo conversion failed', err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // BUG #1 fix: close on Escape key
  useEffect(() => {
    if (!transaction) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [transaction, onClose]);

  if (!transaction) return null;

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(amount));
  };

  const fullDateTime = new Date(transaction.date).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });

  const totalQuantity = (transaction.items || []).reduce((acc, item) => acc + item.quantity, 0);

  const itemSubtotal = (transaction.items || []).reduce((acc, item) => {
    const effectivePrice = item.price * (1 - (item.discount || 0) / 100);
    return acc + (effectivePrice * item.quantity);
  }, 0);

  const orderDiscountPercent = transaction.orderDiscount || transaction.discount || 0;
  const orderDiscountAmount = itemSubtotal * (orderDiscountPercent / 100);

  const handlePrint = async () => {
    try {
      const content = document.getElementById('receipt-content')?.innerHTML;

      if (!content) {
        addToast('error', 'Print Error', 'Receipt content not found');
        return;
      }
      await connectQZ();

      const config = qz.configs.create("PRP-80250B", {
        margins: 0,
        size: { width: 80 },
        units: 'mm',
        interpolation: 'none'
      });

      const printData = [
        {
          type: 'html',
          format: 'plain',
          data: `
            <html>
              <head>
                <script src="https://cdn.tailwindcss.com"></script>
                <style>
                  @page { margin: 0; }
                  html, body { 
                    margin: 0 !important; 
                    padding: 0 !important; 
                    width: 80mm;
                    background: white;
                  }
                  #print-container { width: 72mm; margin: 0 auto; padding-top: 5px; }
                  * { color: black !important; font-family: 'Inter', sans-serif !important; }
                  img { display: block !important; margin: 0 auto !important; max-width: 140px !important; height: auto !important; }
                </style>
              </head>
              <body>
                <div id="print-container">
                  ${content}
                </div>
              </body>
            </html>
          `
        }
      ];

      await qz.print(config, printData);
      addToast('success', 'Print Success', 'Receipt sent to PRP-80250B');
    } catch (err: any) {
      console.error('FULL PRINT ERROR:', err);  // ADD THIS
      console.error('STACK:', err?.stack);       // ADD THIS
      addToast('error', 'Printer Error', err.message);
    }
  };

  return (
    // BUG #2 fix: backdrop click closes the modal
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200 text-black" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

        {/* <div className={`p-3 text-center text-white ${transaction.isPartial ? 'bg-amber-500' : 'bg-emerald-500'}`}>
          <h2 className="text-[10px] font-black uppercase tracking-[0.2em]">
            {transaction.isPartial ? 'Partial Settlement' : 'Order Settled x19'}
          </h2>
        </div> */}

        <div id="receipt-content" className="bg-white p-4 flex-1 overflow-y-auto">
          <div className="flex flex-col items-center justify-center text-center mb-4">
            <img
              src={logoBase64 || Logo}
              style={{ width: '120px', height: 'auto', display: 'block', margin: '0 auto' }}
              className="mb-2 object-contain"
              alt="Logo"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <p className="text-[8px] text-gray-400 font-bold uppercase tracking-[0.2em]">Official Store Receipt</p>
          </div>

          <div className="border-t-2 border-black pt-2 mt-2"></div>

          <div className="space-y-1 mb-3">
            <div className="flex justify-between text-[9px] font-bold">
              <span className="text-gray-400 uppercase">Date & Time</span>
              <span className="text-gray-900">{fullDateTime}</span>
            </div>
            <div className="flex justify-between text-[9px] font-bold">
              <span className="text-gray-400 uppercase">Order ID</span>
              <span className="text-gray-900">#{transaction.id}</span>
            </div>
            <div className="flex justify-between text-[9px] font-bold">
              <span className="text-gray-400 uppercase">Cashier</span>
              <span className="text-gray-900">{transaction.cashierName}</span>
            </div>
            <div className="flex justify-between text-[9px] font-bold">
              <span className="text-gray-400 uppercase">Customer</span>
              <span className="text-gray-900">{transaction.customer?.name || 'Walk-in'}</span>
            </div>
          </div>

          <div className="border-t-2 border-black pt-2 mt-2"></div>

          <div className="w-full mb-2">
            <div className="flex border-b border-black pb-1 mb-1 text-[9px] font-black uppercase">
              <span className="w-[10%]">Sr</span>
              <span className="w-[50%]">Product</span>
              <span className="w-[15%] text-right">Qty</span>
              <span className="w-[25%] text-right">Total</span>
            </div>

            {(transaction.items || []).map((item, index) => {
              const effectiveItemPrice = item.price * (1 - (item.discount || 0) / 100);
              return (
                <div key={index} className="flex py-1.5 text-[10px] items-start border-b border-gray-50">
                  <span className="w-[10%] text-gray-400 font-bold">{index + 1}</span>
                  <div className="w-[50%] flex flex-col">
                    <span className="font-black text-gray-900 uppercase leading-tight">{item.name}</span>
                    <span className="text-[8px] text-gray-500 font-bold uppercase">SIZE: {item.selectedSize.size}</span>
                    {(item.discount || 0) > 0 && (
                      <span className="text-[7px] text-rose-500 font-black uppercase">
                        {item.discount}% off — was Rs {formatAmount(item.price * item.quantity)}
                      </span>
                    )}
                  </div>
                  <span className="w-[15%] text-right font-bold">{item.quantity}</span>
                  <span className="w-[25%] text-right font-black">{formatAmount(effectiveItemPrice * item.quantity)}</span>
                </div>
              );
            })}
          </div>

          {/* 🟢 INCREASED FONT FOR TOTAL QUANTITY */}
          <div className="border-t-2 border-black pt-2 mt-2">
            <div className="flex justify-between">
              <span className="text-[11px] font-black uppercase">Total Quantity</span>
              <span className="text-[12px] font-black">{totalQuantity}</span>
            </div>
          </div>

          {/* Discounts section */}
          {((transaction.items || []).some(i => (i.discount || 0) > 0) || orderDiscountPercent > 0) && (
            <div className="mt-2 border-t border-dashed border-gray-200 pt-2">
              <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Discounts</p>
              <div className="space-y-1">
                {(transaction.items || []).map((item, idx) => {
                  const itemDisc = item.discount || 0;
                  const saved = item.price * item.quantity * (itemDisc / 100);
                  return (
                    <div key={idx} className="flex justify-between text-[8px]">
                      <span className="text-gray-500 font-bold uppercase truncate max-w-[55%]">
                        {item.name} ({item.selectedSize.size})
                      </span>
                      {itemDisc > 0 ? (
                        <span className="font-black text-rose-500">{itemDisc}% — saved Rs {formatAmount(saved)}</span>
                      ) : (
                        <span className="text-gray-300 font-bold">No discount</span>
                      )}
                    </div>
                  );
                })}
                {orderDiscountPercent > 0 && (
                  <div className="flex justify-between text-[8px] pt-1 border-t border-gray-100 mt-1">
                    <span className="text-gray-500 font-black uppercase">Order Discount</span>
                    <span className="font-black text-rose-500">{orderDiscountPercent}% — saved Rs {formatAmount(orderDiscountAmount)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="border-t-2 border-black pt-2 mt-2"></div>

          {/* 🟢 INCREASED FONT FOR FINANCIAL BREAKDOWN */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-[10.5px] font-bold">
              <span className="text-gray-400 uppercase">Gross Subtotal</span>
              <span className="font-black text-gray-900">Rs {formatAmount(itemSubtotal)}</span>
            </div>

            {orderDiscountPercent > 0 && (
              <div className="flex justify-between text-[10.5px] font-bold text-gray-900">
                <span className="uppercase">Discount ({orderDiscountPercent}%)</span>
                <span className="font-black">- Rs {formatAmount(orderDiscountAmount)}</span>
              </div>
            )}

            <div className="border-t-2 border-black pt-2 mt-2">
              <div className="flex justify-between items-baseline">
                <span className="text-[10px] font-black uppercase flex items-center gap-1">
                  Net Total
                  {orderDiscountPercent === 100 && (
                    <span className="ml-1 text-[7px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-1.5 py-0.5 rounded">Complimentary</span>
                  )}
                </span>
                {/* 🟢 DECREASED FONT FOR NET TOTAL AMOUNT */}
                <span className="text-l font-black tracking-tighter">Rs {formatAmount(transaction.total)}</span>
              </div>
            </div>
            {transaction.paymentMethod === 'Bank Transfer' ? (
              <div className="border-t-2 border-black pt-2 mt-2">
                <div className="flex justify-between text-[10.5px] font-bold uppercase">
                  <span className="text-gray-400">Paid Via</span>
                  <span className="font-black">{transaction.paymentMethod}</span>
                </div>
                <div className="flex justify-between text-[10.5px] font-bold uppercase">
                  <span className="text-gray-400">REFERENCE NO</span>
                  <span className="font-black">{transaction.referenceNumber}</span>
                </div>
              </div>
            ) : (
              <div className="border-t-2 border-black pt-2 mt-2 space-y-1">
                <div className="flex justify-between text-[10.5px] font-bold uppercase">
                  <span className="text-gray-400">Paid Via</span>
                  <span className="font-black">{transaction.paymentMethod}</span>
                </div>
                {/* BUG #6/#22 fix: show cash tendered and change due for cash transactions */}
                {transaction.paymentMethod === 'Cash' && (transaction.cashReceived ?? 0) > 0 && (
                  <>
                    <div className="flex justify-between text-[10.5px] font-bold uppercase">
                      <span className="text-gray-400">Cash Received</span>
                      <span className="font-black">Rs {formatAmount(transaction.cashReceived ?? 0)}</span>
                    </div>
                    <div className="flex justify-between text-[10.5px] font-black uppercase border-t border-dashed border-gray-200 pt-1 mt-1">
                      <span className="text-gray-900">Change Due</span>
                      <span className="font-black text-emerald-700">Rs {formatAmount(transaction.changeAmount ?? 0)}</span>
                    </div>
                  </>
                )}
              </div>
            )
            }
          </div>

          <div className="border-t-2 border-black pt-2 mt-2"></div>

          <div className="flex flex-col items-center text-center">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${transaction.id}`}
              className="w-16 h-16 mix-blend-multiply opacity-90 mb-4"
              alt="QR"
            />
            <div className="space-y-1">
              <p className="text-[9px] font-black text-gray-900 uppercase">NiaMia</p>
              <p className="text-[8px] font-bold text-gray-500 leading-tight uppercase">
                D18 Block 4 Clifton Scheme<br />
                Karachi, Pakistan
              </p>
            </div>
            <p className="text-[8px] font-black text-gray-400 uppercase mt-4 tracking-widest border-t border-gray-100 pt-2 w-full">Thank you for shopping</p>

            <div className="mt-2 p-1 border-2 border-black w-full">
              <p className="text-[9px] font-black text-black uppercase tracking-tighter italic">
                No refunds and exchanges allowed
              </p>
            </div>
          </div>
        </div>

        <div className="p-3 bg-gray-50 border-t border-gray-100 flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 bg-white border border-gray-200 text-gray-500 rounded-xl text-[10px] font-black uppercase hover:bg-gray-100 transition-colors">
            Close
          </button>
          <button onClick={handlePrint} className="flex-1 py-3 bg-black text-white rounded-xl text-[10px] font-black uppercase flex items-center justify-center gap-2 transition-all active:scale-95">
            <Printer size={14} /> Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;